import { css, html, LitElement } from "lit"
import { genererIdentifiantAleatoire, naviguerVers } from "../transverse/Utilitaire.js"
import "@luminecence/composants/BoutonIcone"
import "../popups/PopupCommunaute.js"
import "@luminecence/composants/PopupConfirmation"
import PopupBase from "@luminecence/composants/PopupBase"

export default class PageCommunaute extends LitElement {
  static properties = { listeCommunautes: { type: Array } }

  static styles = css`
  ul {
    list-style-type: none;
    padding: 0;
  }

  ul li:not(:last-child) {
    margin-bottom: 3rem;
  }

  li {
    --taille-icone-bouton: 24px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    padding-left: 1rem;
    background-color: var(--couleur-200);
  }

  .nom-communaute {
    display: flex;
    align-items: center;
    font-weight: bold;
    border-bottom: 1px solid var(--couleur-texte);
    padding-bottom: 0.5rem;
    gap: 1rem;
  }

  .zone-communaute {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .ajouter-communaute {
    --taille-icone-bouton: 4em;
  }
  `

  constructor() {
    super()

    this.listeCommunautes = []
  }

  async connectedCallback() {
    super.connectedCallback()
    await this.recupererCommunautes()
  }

  async recupererCommunautes() {
    try {
      const communautes = await window.authentification.bdd.communautes.recupererCommunautes()
      const uid = window.authentification.getUser().uid

      // On enrichit chaque communauté avec l'information admin
      this.listeCommunautes = await Promise.all(
        communautes.map(async (communaute) => {
          const role = await window.authentification.bdd.communautes.recupererLeRoleDunMembreDansUneCommunaute(communaute.id, uid)
          return { ...communaute, estMembreAdmin: role === "admin" }
        }),
      )
    } catch (error) {
      console.error("Erreur lors de la récupération des communautés :", error)
    }
  }

  render() {
    return html`
    <div class="zone-communaute">
      <h1>Liste des communautés</h1>
      <p-bouton-icone class="ajouter-communaute" .icone=${"ajouter_communaute"} @click=${this.ajouterCommunaute}></p-bouton-icone>
    </div>
      
      ${this.listeCommunautes.length > 0 ? this.afficherListeCommunautes() : html`<p>Aucune communauté disponible pour le moment.</p>`}
      `
  }

  afficherListeCommunautes() {
    return html`
    <ul>
      ${this.listeCommunautes.map((communaute) => this.afficherCommunaute(communaute))}
    </ul>
    `
  }

  afficherCommunaute(communaute) {
    return html`
    <li>
      <div class="nom-communaute">
        ${communaute.nom}
        ${communaute.estMembreAdmin ? this.gererVisionAdmin(communaute) : ""}
        <p-bouton-icone data-id=${communaute.id} id="rejoindre-communaute" .icone=${"rejoindre"} @click=${(event) => this.rejoindreCommunaute(communaute, event)}></p-bouton-icone>
      </div>
      <span>${communaute.description}</span>
  </li>
    `
  }

  gererVisionAdmin(communaute) {
    return html`
    <p-bouton-icone id="editer-communaute" .icone=${"editer"} @click=${(event) => this.gererCommunaute(event, communaute)}></p-bouton-icone>
    <p-bouton-icone id="supprimer-communaute" .icone=${"supprimer"} @click=${() => this.supprimerCommunaute(communaute)}></p-bouton-icone>
    `
  }

  async rejoindreCommunaute(communaute, event) {
    event.stopPropagation()
    const membres = (await window.authentification.bdd.communautes.recupererLesMembresDuneCommunaute(communaute.id)).map((membre) => membre.id),
      idMembreConnecte = window.authentification.getUser().uid
    if (membres.includes(idMembreConnecte)) {
      naviguerVers(`/communaute/${communaute.id}/echanges`)
      return
    } else {
      const communauteCourante = await window.authentification.bdd.communautes.recupererUneCommunaute(communaute.id)
      if (communauteCourante.code === "") {
        this.admettreUnMembreDansLaCommunaute(communauteCourante)
        return
      }
      event.stopPropagation()
      const resultat = await PopupBase.ouvrirEtAttendre("p-popup-communaute", {
        estModeRejoindre: true,
        id: communaute.id,
      })

      if (resultat) {
        const { admis } = resultat

        if (!admis) {
          this.erreur = "Le code que vous avez saisi est incorrect. Vous ne pouvez pas rejoindre cette communauté."
          return
        }
        this.admettreUnMembreDansLaCommunaute(communauteCourante)
      }
    }
  }

  admettreUnMembreDansLaCommunaute(communaute) {
    const idCommunaute = communaute.id
    const communauteAvecRole = { ...communaute, estMembreAdmin: false }
    this.listeCommunautes.push(communauteAvecRole)
    this.listeCommunautes = [...this.listeCommunautes]
    window.authentification.bdd.communautes.ajouterUnMembreDansUneCommunite(idCommunaute, window.authentification.getUser().uid)
    naviguerVers(`/communaute/${idCommunaute}/echanges`)
  }

  async ajouterCommunaute(event) {
    event.stopPropagation()
    const resultat = await PopupBase.ouvrirEtAttendre("p-popup-communaute")

    if (resultat) {
      const { nom, code, description } = resultat,
        id = genererIdentifiantAleatoire(),
        communaute = {
          id,
          nom,
          code,
          description,
          estMembreAdmin: true,
        }

      this.listeCommunautes.push(communaute)
      this.listeCommunautes = [...this.listeCommunautes]
      window.authentification.bdd.communautes.ajouterUneCommunaute(communaute)
    }
  }

  async gererCommunaute(event, communaute) {
    event.stopPropagation()
    const resultat = await PopupBase.ouvrirEtAttendre("p-popup-communaute", { id: communaute.id, estModeModification: true })

    if (resultat) {
      const { nom, code, description } = resultat,
        communite = this.listeCommunautes.find((communauteListee) => communauteListee.id === communaute.id)
      communite.nom = nom
      communite.code = code
      communite.description = description
      this.listeCommunautes = [...this.listeCommunautes]
      window.authentification.bdd.communautes.ajouterUneCommunaute(communite)
    }
  }

  async supprimerCommunaute(communaute) {
    const optionsPopup = {
      titre: "Confirmer la suppression de la communauté",
      texte: "Êtes-vous sûr de vouloir supprimer cette communauté ?",
    }
    const resultat = await PopupBase.ouvrirEtAttendre("p-popup-confirmation", optionsPopup)
    if (resultat) {
      this.listeCommunautes = this.listeCommunautes.filter((c) => c.id !== communaute.id)
      window.authentification.bdd.communautes.supprimerUneCommunaute(communaute.id)
    }
  }
}
customElements.define("p-page-communaute", PageCommunaute)
